import { ApolloClient, HttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { appConfig, realtimeUrlFromAppsyncUrl } from '../config';
import { getAuthToken } from '../auth/amplify';

// AppSync expects the Cognito JWT in the `Authorization` header for the
// COGNITO_USER_POOLS auth mode. We attach a freshly-resolved token to every
// request; Amplify refreshes the token transparently when it is expired.
const authLink = setContext(async (_operation, { headers }) => {
  const token = await getAuthToken();
  return {
    headers: {
      ...(headers as Record<string, string> | undefined),
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const httpLink = new HttpLink({ uri: appConfig.appsyncUrl });

// AppSync's real-time endpoint authenticates via connectionParams. We resolve a
// fresh token for each connection (and each subsequent connection after a
// reconnect) so long-lived subscriptions keep a valid JWT.
const wsLink = new GraphQLWsLink(
  createClient({
    url: realtimeUrlFromAppsyncUrl(appConfig.appsyncUrl),
    connectionParams: async () => {
      const token = await getAuthToken();
      return token ? { Authorization: token } : {};
    },
  }),
);

// Route subscription operations over the WebSocket link and everything else
// (queries/mutations) over the authenticated HTTP link.
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  authLink.concat(httpLink),
);

export function createApolloClient(): ApolloClient<unknown> {
  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
  });
}
